// WP-07 — O-01 Sign-in (UX v1.4; CR-002 / D-MT-1). Tenant before credentials:
// on {slug}.steier.co.za the card goes straight to email/password (the API
// resolves the tenant from the Host header); on the bare domain a COMPANY
// CODE step comes first — never an email-based tenant picker (no enumeration).
// Plain controlled inputs (D-WP07-2): RHF+Zod arrive with the form-heavy
// verticals; two fields do not warrant the dependency in the shell.
//
// WP-T-2 (UX v1.6 O-01, §3 Theme B): the card is the §10.9 Titled Card surface
// with a brand header on colour.identity / on.identity (pre-auth = the Steier
// default palette) and the §10.6 token inputs. Presentation only — the two
// forms, their fields, names and the login call are untouched.
//
// UX-4a (UX v1.7 §8): the card renders inside the page's <main> landmark
// (UX2-O01-3), and after "Continue" focus is moved to Email explicitly
// (UX2-O01-2 — the browser left it on BODY once the code form unmounted).
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient } from "../api/api-client";
import { AuthService } from "../auth/auth-service";
import { AppError } from "../api/errors";
import { resolveCurrentTenant } from "../auth/tenant";
import { t } from "../i18n/i18n";
import { displayName } from "../theme/branding";
import { TitledCard } from "../components/titled-card";
import { documentTitle, useDocumentTitle } from "../app/document-title";

const authService = new AuthService(apiClient);

export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const tenant = resolveCurrentTenant();

  const [companyCode, setCompanyCode] = useState("");
  const [codeConfirmed, setCodeConfirmed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  // UX2-A11Y-1: "Sign in — Steier" (no tenant until the session exists).
  useDocumentTitle(documentTitle(t("signIn.heading")));
  const continued = useRef(false);

  // UX2-O01-2: hand focus to Email once the code step has been confirmed.
  // Only after a real Continue — a subdomain first load keeps autoFocus.
  useEffect(() => {
    if (!codeConfirmed || !continued.current) return;
    const frame = requestAnimationFrame(() => emailRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [codeConfirmed]);

  const onSubdomain = tenant.kind === "subdomain";
  const askCompanyCode = !onSubdomain && !codeConfirmed;
  const brand = displayName(
    {},
    onSubdomain ? tenant.slug : codeConfirmed ? companyCode : undefined,
  );

  const onContinue = (event: FormEvent) => {
    event.preventDefault();
    if (companyCode.trim().length > 0) {
      continued.current = true;
      setCodeConfirmed(true);
      setError(null);
    }
  };

  const onSignIn = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await authService.login(
        email,
        password,
        onSubdomain ? undefined : companyCode.trim().toLowerCase(),
      );
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? "/", { replace: true });
    } catch (err) {
      setError(err instanceof AppError ? err.message : t("errors.network"));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="sign-in">
      <TitledCard as="div" className="sign-in__card">
        <div className="sign-in__brand">
          <p className="sign-in__brand-name">{brand}</p>
        </div>
        <div className="sign-in__body">
          <h1>{t("signIn.heading")}</h1>
          {error ? (
            <p className="error-banner" role="alert">
              {error}
            </p>
          ) : null}
          {askCompanyCode ? (
            <form onSubmit={onContinue}>
              <label htmlFor="companyCode">
                {t("signIn.companyCodeLabel")}
              </label>
              <input
                id="companyCode"
                name="companyCode"
                autoComplete="organization"
                autoFocus
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value)}
                aria-describedby="companyCodeHelp"
                required
              />
              <p id="companyCodeHelp" className="sign-in__help">
                {t("signIn.companyCodeHelp")}
              </p>
              <div className="sign-in__actions">
                <button type="submit" className="button">
                  {t("signIn.continue")}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={(e) => void onSignIn(e)}>
              <label htmlFor="email">{t("signIn.emailLabel")}</label>
              <input
                id="email"
                name="email"
                type="email"
                ref={emailRef}
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label htmlFor="password">{t("signIn.passwordLabel")}</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="sign-in__actions">
                <button type="submit" className="button" disabled={pending}>
                  {pending ? t("signIn.pending") : t("signIn.submit")}
                </button>
                {!onSubdomain ? (
                  <button
                    type="button"
                    className="button button--ghost"
                    onClick={() => {
                      setCodeConfirmed(false);
                      setError(null);
                    }}
                  >
                    {t("signIn.changeCompany")}
                  </button>
                ) : null}
              </div>
            </form>
          )}
        </div>
      </TitledCard>
    </main>
  );
}
